import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';

type EntityRow = {
  entity_id: string;
  tenant_id: string;
  name: string;
  type?: string;
};

export default function AdminEntitiesPage() {
  const { activeTenantId, activeEntityId, setActiveEntityId, setActiveConfigId, refresh } = useBusiness();
  const [rows, setRows] = useState<EntityRow[]>([]);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('clinic');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = async () => {
    if (!activeTenantId) return;
    setError('');
    const resp = await apiFetch(`/api/business/entities?tenant_id=${encodeURIComponent(activeTenantId)}`);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    const data = (await resp.json()) as EntityRow[];
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    void load();
  }, [activeTenantId]);

  const createEntity = async () => {
    if (!activeTenantId || !name.trim()) return;
    const resp = await apiFetch('/api/business/entities', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: activeTenantId, name: name.trim(), type: kind.trim() || 'clinic' }),
    });
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    setName('');
    setInfo('Entity created. Click Use to activate it.');
    await load();
    await refresh();
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }}>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }} gutterBottom>
        Entities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create facilities and switch the active entity context for scoped testing.
      </Typography>
      {!activeTenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a tenant first in the Tenants tab.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Entity name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ minWidth: 240 }}
          disabled={!activeTenantId}
        />
        <TextField
          select
          size="small"
          label="Type"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          sx={{ minWidth: 180 }}
          disabled={!activeTenantId}
        >
          {['clinic', 'hospital', 'private', 'public_health', 'individual', 'ehr', 'emr'].map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={createEntity} disabled={!activeTenantId || !name.trim()}>
          Create
        </Button>
        <Button variant="outlined" onClick={() => void load()} disabled={!activeTenantId}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Box sx={{ border: '1px solid #e6ebf1', borderRadius: 1.5, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Entity ID</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.entity_id}>
                <TableCell>
                  <Button
                    size="small"
                    variant={activeEntityId === r.entity_id ? 'contained' : 'outlined'}
                    onClick={() => {
                      setActiveEntityId(r.entity_id);
                      setActiveConfigId(null);
                      setInfo(`Active entity set to: ${r.name}`);
                    }}
                  >
                    {activeEntityId === r.entity_id ? 'Active' : 'Use'}
                  </Button>
                </TableCell>
                <TableCell>{r.entity_id}</TableCell>
                <TableCell>{r.tenant_id}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.type || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

